'use client'

import { Button } from "@/components/ui/button"
import Link from "next/link"
import { useCompetitionContext } from "../providers/CompetitionContext"
import { getCompetitionCopy } from "../lib/competitionCopy"

type FinalBonusColumnKey = "jaune" | "vert" | "pois" | "blanc";

const finalStandingsBonusColumns: { key: FinalBonusColumnKey; label: string }[] = [
  { key: "jaune", label: "Maillot jaune" },
  { key: "vert", label: "Maillot vert" },
  { key: "pois", label: "Maillot à pois" },
  { key: "blanc", label: "Maillot blanc" },
];

const finalStandingsBonusRows: ({ place: number } & Record<FinalBonusColumnKey, number>)[] = [
  { place: 1, jaune: 60, vert: 30, pois: 30, blanc: 30 },
  { place: 2, jaune: 40, vert: 20, pois: 20, blanc: 20 },
  { place: 3, jaune: 30, vert: 18, pois: 18, blanc: 18 },
  { place: 4, jaune: 25, vert: 17, pois: 17, blanc: 17 },
  { place: 5, jaune: 22, vert: 16, pois: 16, blanc: 16 },
  { place: 6, jaune: 20, vert: 15, pois: 15, blanc: 15 },
  { place: 7, jaune: 18, vert: 14, pois: 14, blanc: 14 },
  { place: 8, jaune: 16, vert: 13, pois: 13, blanc: 13 },
  { place: 9, jaune: 14, vert: 12, pois: 12, blanc: 12 },
  { place: 10, jaune: 12, vert: 11, pois: 11, blanc: 11 },
  { place: 11, jaune: 10, vert: 10, pois: 10, blanc: 10 },
  { place: 12, jaune: 9, vert: 9, pois: 9, blanc: 9 },
  { place: 13, jaune: 8, vert: 8, pois: 8, blanc: 8 },
  { place: 14, jaune: 7, vert: 7, pois: 7, blanc: 7 },
  { place: 15, jaune: 6, vert: 6, pois: 6, blanc: 6 },
  { place: 16, jaune: 5, vert: 5, pois: 5, blanc: 5 },
  { place: 17, jaune: 4, vert: 4, pois: 4, blanc: 4 },
  { place: 18, jaune: 3, vert: 3, pois: 3, blanc: 3 },
  { place: 19, jaune: 2, vert: 2, pois: 2, blanc: 2 },
  { place: 20, jaune: 1, vert: 1, pois: 1, blanc: 1 },
];

function FinalBonusShirtIcon({ type }: { type: FinalBonusColumnKey }) {
  const fillByType: Record<FinalBonusColumnKey, string> = {
    jaune: "#FDC300",
    vert: "#2AA54B",
    pois: "#FFFFFF",
    blanc: "#FFFFFF",
  };

  return (
    <svg viewBox="0 0 24 20" className="shirt-svg" aria-hidden="true" focusable="false">
      <path
        d="M8 2L10 0H14L16 2L19 1L22 5L20 7L18 5V18H6V5L4 7L2 5L5 1L8 2Z"
        fill={fillByType[type]}
        stroke="rgba(0, 0, 0, 0.35)"
        strokeWidth="1"
      />
      {type === "pois" && (
        <>
          <circle cx="12" cy="6.5" r="1.1" fill="#D62828" />
          <circle cx="9.2" cy="9.5" r="1.1" fill="#D62828" />
          <circle cx="14.8" cy="9.5" r="1.1" fill="#D62828" />
          <circle cx="12" cy="12.8" r="1.1" fill="#D62828" />
          <circle cx="9.2" cy="15.2" r="1" fill="#D62828" />
          <circle cx="14.8" cy="15.2" r="1" fill="#D62828" />
        </>
      )}
    </svg>
  );
}


export default function RulesPage() {
    const { competitionConfig } = useCompetitionContext();
    const copy = getCompetitionCopy(competitionConfig?.competitionFeed);
    const stage = copy.raceLabelSingular;
    const stagesLabel = copy.raceLabelPlural;
    const stageWithArticle = copy.raceLabelWithArticle;
    const Stages = copy.raceLabelPluralTitle;

    const raceStages = [
    { stageNumber: 1, start: "Barcelone", end: "Barcelone", distance: "19.6 km", terrain: "clm-equipe" },
    { stageNumber: 2, start: "Tarragone", end: "Barcelone", distance: "168.5 km", terrain: "accidente" },
    { stageNumber: 3, start: "Granollers", end: "Les Angles", distance: "195.9 km", terrain: "montagne" },
    { stageNumber: 4, start: "Carcassonne", end: "Foix", distance: "181.9 km", terrain: "accidente" },
    { stageNumber: 5, start: "Lannemezan", end: "Pau", distance: "158.3 km", terrain: "plaine" },
    { stageNumber: 6, start: "Pau", end: "Gavarnie-Gèdre", distance: "186.2 km", terrain: "montagne" },
    { stageNumber: 7, start: "Hagetmau", end: "Bordeaux", distance: "175.1 km", terrain: "plaine" },
    { stageNumber: 8, start: "Périgueux", end: "Bergerac", distance: "180.4 km", terrain: "plaine" },
    { stageNumber: 9, start: "Malemort", end: "Ussel", distance: "185.5 km", terrain: "accidente" },
    { restDayNumber: 1, location: "Cantal", restDate: "Lun 13/07/2026" },
    { stageNumber: 10, start: "Aurillac", end: "Le Lioran", distance: "166.6 km", terrain: "montagne" },
    { stageNumber: 11, start: "Vichy", end: "Nevers", distance: "161.3 km", terrain: "plaine" },
    { stageNumber: 12, start: "Circuit Nevers Magny-Cours", end: "Chalon-sur-Saône", distance: "179.1 km", terrain: "plaine" },
    { stageNumber: 13, start: "Dole", end: "Belfort", distance: "205.8 km", terrain: "accidente" },
    { stageNumber: 14, start: "Mulhouse", end: "Le Markstein Fellering", distance: "155.3 km", terrain: "montagne" },
    { stageNumber: 15, start: "Champagnole", end: "Plateau de Solaison", distance: "183.9 km", terrain: "montagne" },
    { restDayNumber: 2, location: "Haute-Savoie", restDate: "Lun 20/07/2026" },
    { stageNumber: 16, start: "Évian-les-Bains", end: "Thonon-les-Bains", distance: "26.1 km", terrain: "clm-individuel" },
    { stageNumber: 17, start: "Chambery", end: "Voiron", distance: "174.7 km", terrain: "plaine" },
    { stageNumber: 18, start: "Voiron", end: "Orcières-Merlette", distance: "185.2 km", terrain: "montagne" },
    { stageNumber: 19, start: "Gap", end: "Alpe d'Huez", distance: "127.9 km", terrain: "montagne" },
    { stageNumber: 20, start: "Le Bourg d'Oisans", end: "Alpe d'Huez", distance: "170.9 km", terrain: "montagne" },
    { stageNumber: 21, start: "Thoiry", end: "Paris Champs-Élysées", distance: "133 km", terrain: "plaine" }
  ];

  const getTerrainLabel = (terrain:any) => {
    switch(terrain) {
      case 'montagne': return 'Montagne';
      case 'accidente': return 'Accidenté';
      case 'plaine': return 'Plaine';
      case 'clm-equipe': return 'CLM équipe';
      case 'clm-individuel': return 'CLM individuel';
      default: return terrain;
    }
  };
  
  return (
    <div className="min-h-[calc(100vh-100px)] bg-[var(--rtbf-dark,#001425)] px-3 pb-20 pt-6 sm:px-4">
      <div className="mx-auto w-full max-w-3xl">
        <section className="space-y-8 py-1 text-white sm:py-0">
          <div className="space-y-6">
            <div>
              <h1 className="mb-2 text-xl font-bold text-white">Règlement {copy.gameName}</h1>
              <p className="text-md text-white/90">
                Bienvenue dans le jeu de fantasy cyclisme “Tour de France, le jeu” !
              </p>
            </div>

            <div className="rules-content">
  <ul>
    <li>Constituez votre équipe idéale avec <strong>15 coureurs</strong>.</li>
    <li>Respectez un <strong>budget de 140 millions d’euros</strong>.</li>
    <li>Sélectionnez <strong>maximum 3 coureurs par équipe</strong>.</li>
  </ul>
  <p>Faites les bons choix tactiques pour viser la victoire.</p>
  <br />
  <h2>1. {Stages} et délais</h2>
  <p>
    Vous trouverez ci-dessous un aperçu de toutes les {stagesLabel}. Les délais exacts sont 
    indiqués dans le calendrier sur la page d'Équipe. Les modifications apportées à 
    votre équipe du Tour doivent être effectuées avant ce délai, sinon elles ne seront 
    prises en compte qu'à partir de {stageWithArticle} suivante.
  </p>
  <br />
  <p>Vous trouverez ci-dessous un aperçu de toutes les {stagesLabel}. Les modifications d'équipe sont détaillées au point 4. Transferts.</p>
<style>{`
        .rules-content p {
          margin-bottom: 1rem;
          line-height: 1.65;
          color: rgba(255, 255, 255, 0.9);
        }

        .rules-content h2 {
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          font-weight: 700;
          color: #ffffff !important;
          font-size: 1.25rem !important;
        }

        .rules-content h3 {
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
          font-weight: 700;
          color: #ffffff;
          font-size: 1.125rem;
        }

        .rules-content ul {
          margin: 0.75rem 0 1rem 1.25rem;
          list-style: disc;
        }

        .rules-content ul li {
          margin-bottom: 0.35rem;
        }

        .rules-content figure {
          margin: 1.25rem 0;
          overflow-x: auto;
          border-radius: 10px;
          background-color: rgba(255, 255, 255, 0.95);
          padding: 0;
        }

        .rules-content table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .rules-content td {
          padding: 0.6rem 0.7rem;
          border: none;
          font-size: 0.9rem;
        }

        .final-bonus-table {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .final-bonus-table table {
          min-width: 100%;
          table-layout: fixed;
        }

        .final-bonus-table thead th {
          background-color: var(--rtbf-blue, #0076A9);
          color: #ffffff;
          font-weight: 700;
          text-align: center;
          padding: 0.6rem 0.7rem;
        }

        .final-bonus-table tbody th,
        .final-bonus-table tbody td {
          text-align: center;
          color: #ffffff;
          font-weight: 400;
          padding: 0.6rem 0.7rem;
        }

        .final-bonus-table tbody tr.bonus-row-even th,
        .final-bonus-table tbody tr.bonus-row-even td {
          background-color: #003D60;
        }

        .final-bonus-table tbody tr.bonus-row-odd th,
        .final-bonus-table tbody tr.bonus-row-odd td {
          background-color: #001324;
        }

        .final-bonus-table thead th:first-child,
        .final-bonus-table tbody th {
          position: sticky;
          left: 0;
          z-index: 1;
        }

        .final-bonus-table thead th:first-child {
          z-index: 2;
        }

        .shirt-svg {
          display: block;
          width: 20px;
          height: 16px;
          margin: 0 auto;
        }

        .final-bonus-header-label {
          display: none;
        }

        .final-bonus-header-icon {
          display: inline-block;
        }

        @media (max-width: 640px) {
          .final-bonus-table thead th,
          .final-bonus-table tbody th,
          .final-bonus-table tbody td {
            padding: 0.5rem;
            font-size: 0.9rem;
          }
        }

        @media (min-width: 768px) {
          .final-bonus-header-label {
            display: inline;
          }

          .final-bonus-header-icon {
            display: none;
          }
        }

        .rules-content br {
          display: none;
        }

        ol.stages-list {
          margin: 1.25rem 0;
          padding: 0;
          list-style: none;
        }
        
        ol.stages-list li {
          margin-bottom: 0.6rem;
          padding: 0.9rem 1rem;
          border-radius: 10px;
          background-color: var(--rtbf-blue, #0076A9);
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: flex-start;
          gap: 1rem;
        }
        
        .stage-info {
          font-weight: 700;
          color: #ffffff;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          font-size: 0.72rem;
          line-height: 1.2;
        }

        ol.stages-list li .stage-info {
          color: var(--rtbf-orange, #fdc300);
        }

        .stage-terrain {
          color: #ffffff;
        }
        
        .distance {
          color: #ffffff;
          font-weight: 700;
          text-align: left;
          font-style: normal;
          width: 100%;
          margin-top: -0.5rem;
          font-size: 0.86rem;
          line-height: 1.25;
        }
        
        .terrain {
          margin-left: 0.35rem;
        }

        ol.stages-list li.rest-day {
          background-color: #e6edf5;
        }

        ol.stages-list li.rest-day .stage-info {
          color: var(--rtbf-blue-dark, #003E63);
        }

        .rest-day .distance {
          color: var(--rtbf-blue-dark, #003E63);
          margin-top: 0;
        }

        @media (min-width: 768px) {
          ol.stages-list li {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }

          .stage-info {
            font-size: 0.9rem;
          }

          .distance {
            text-align: right;
            width: auto;
            margin-top: 0;
            font-size: 1rem;
          }
        }

        .points-pill {
          display: inline;
          margin-left: 0.2rem;
          color: #FDC300;
          font-weight: 700;
        }
      `}</style>

      <ol className="stages-list">
        {raceStages.map((stageItem, index) => (
          <li key={index} className={stageItem.restDayNumber ? "rest-day" : undefined}>
            {stageItem.restDayNumber ? (
              <>
                <span className="stage-info">
                  Repos {stageItem.restDayNumber}: {stageItem.location}
                </span>
                <span className="distance">{stageItem.restDate}</span>
              </>
            ) : (
              <>
                <span className="stage-info">
                  Étape {stageItem.stageNumber} | <span className="stage-terrain">{getTerrainLabel(stageItem.terrain)}</span>
                </span>
                <span className="distance">
                  {stageItem.start} - {stageItem.end} / {stageItem.distance}
                </span>
              </>
            )}
          </li>
        ))}
      </ol>

<p>Votre équipe définitive doit être validée avant le départ de la 1re {stage}.</p>
  <h2>2. Gagner des points</h2>
  <p>Après chaque {stage}, le top 20 de {stageWithArticle} marque des points :</p>
  <h3>2.1 Points par étape</h3>
  <br />
  <figure className="table">
    <table cellPadding={10} cellSpacing={0}>
      <tbody>
        <tr>
          <td style={{ backgroundColor: "#0076A9", textAlign: "center" }}>
            <span style={{ color: "#FFFFFE" }}>
              <strong>Place</strong>
            </span>
          </td>
          <td style={{ backgroundColor: "#0076A9", textAlign: "center" }}>
            <span style={{ color: "#FFFFFE" }}>
              <strong>Points</strong>
            </span>
          </td>
          <td style={{ backgroundColor: "#0076A9", textAlign: "center" }}>
            <span style={{ color: "#FFFFFE" }}>
              <strong>Place</strong>
            </span>
          </td>
          <td style={{ backgroundColor: "#0076A9", textAlign: "center" }}>
            <span style={{ color: "#FFFFFE" }}>
              <strong>Points</strong>
            </span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>1</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>30</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>11</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>10</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>2</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>20</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>12</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>9</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>3</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>18</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>13</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>8</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>4</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>17</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>14</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>7</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>5</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>16</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>15</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>6</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>6</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>15</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>16</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>5</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>7</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>14</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>17</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>4</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>8</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>13</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>18</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>3</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>9</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>12</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>19</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>2</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>10</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>11</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>20</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>1</span>
          </td>
        </tr>
      </tbody>
    </table>
  </figure>
  <h3>2.2 Points contre-la-montre par équipes</h3>
  <p>
    Pour le contre-la-montre par équipes (TTT), tous les coureurs d’une même équipe qui prennent le départ reçoivent le même nombre de points.
  </p>
  <p>
    Exemple : si UAE termine 1re et Visma 2e, alors Pogacar et Wellens reçoivent tous deux 15 points, tandis que Campenaerts et Vingegaard en reçoivent chacun 10.
  </p>
  <figure className="table">
    <table cellPadding={10} cellSpacing={0}>
      <tbody>
        <tr>
          <td style={{ backgroundColor: "#0076A9", textAlign: "center" }}>
            <span style={{ color: "#FFFFFE" }}>
              <strong>Place équipe (TTT)</strong>
            </span>
          </td>
          <td style={{ backgroundColor: "#0076A9", textAlign: "center" }}>
            <span style={{ color: "#FFFFFE" }}>
              <strong>Points</strong>
            </span>
          </td>
          <td style={{ backgroundColor: "#0076A9", textAlign: "center" }}>
            <span style={{ color: "#FFFFFE" }}>
              <strong>Place équipe (TTT)</strong>
            </span>
          </td>
          <td style={{ backgroundColor: "#0076A9", textAlign: "center" }}>
            <span style={{ color: "#FFFFFE" }}>
              <strong>Points</strong>
            </span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>1</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>15</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>6</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>5</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>2</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>10</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>7</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>4</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>3</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>8</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>8</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>3</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>4</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>7</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>9</span>
          </td>
          <td style={{ backgroundColor: "#001324", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>2</span>
          </td>
        </tr>
        <tr>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>5</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>6</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>10</span>
          </td>
          <td style={{ backgroundColor: "#003D60", textAlign: "center" }}>
            <span style={{ color: "#FFFFFF" }}>1</span>
          </td>
        </tr>
      </tbody>
    </table>
  </figure>
  <br />
  <h3>2.3 Bonus : combativité</h3>
  <p>
    Le ou les combatif(s) du jour marque(nt)
    <span className="points-pill">+5 points</span>.
  </p>
  <br />
  <h3>2.4 Porteurs de maillots</h3>
  <p>
    Après chaque {stage} :
  </p>
  <ul>
    <li>le porteur du maillot jaune marque <span className="points-pill">+8 points</span>,</li>
    <li>le porteur du maillot vert marque <span className="points-pill">+5 points</span>,</li>
    <li>le porteur du maillot à pois marque <span className="points-pill">+5 points</span>,</li>
    <li>le porteur du maillot blanc marque <span className="points-pill">+5 points</span>.</li>
  </ul>
  <br />
  <h3>2.5 Points bonus pour les classements finaux</h3>
  <p>Après l’arrivée à Paris, les vainqueurs finaux des quatre classements seront 
    mis à l’honneur. Ces points bonus seront ajoutés au total de points (et non à la 
    dernière journée de jeu), aussi bien dans le classement général que dans les ligues.
  </p>
  <figure className="table final-bonus-table">
    <table aria-label="Points bonus pour les classements finaux">
      <thead>
        <tr>
          <th scope="col">Place</th>
          {finalStandingsBonusColumns.map((column) => (
            <th key={column.key} scope="col">
              <span className="final-bonus-header-label">{column.label}</span>
              <span className="final-bonus-header-icon">
                <FinalBonusShirtIcon type={column.key} />
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {finalStandingsBonusRows.map((row, index) => (
          <tr key={row.place} className={index % 2 === 0 ? "bonus-row-even" : "bonus-row-odd"}>
            <th scope="row">{row.place}</th>
            {finalStandingsBonusColumns.map((column) => (
              <td key={`${row.place}-${column.key}`}>{row[column.key]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </figure>
  <br />
  <h3>2.6 Points bonus pour le Super-Combatif</h3>
  <p>
    Le coureur le plus combatif du Tour reçoit <span className="points-pill">+10 points</span>. Au cours des quatre dernières 
    années, le Super-Combatif a été décerné à Wout van Aert (2022), 
    Victor Campenaerts (2023), Richard Carapaz (2024) et Ben Healy (2025).
  </p>
  <br />
  <h2>3. Joker</h2>
  <p>
    Le Joker est un bonus unique que vous pouvez utiliser <strong>une seule fois</strong> pendant la 
    compétition. Il s’applique à un seul coureur, pour une {stage} spécifique de votre choix.
    Il doit être validé par vos soins avant le départ de {stageWithArticle} sur laquelle vous souhaitez l’utiliser.
  </p>
  <p>Lorsque vous activez le Joker sur un de vos coureurs, tous les points qu’il gagne 
    lors de cette {stage} sont <strong>doublés</strong>, qu’il s’agisse de points pour sa place dans le 
    classement de {stageWithArticle}, des maillots qu’il porte ou de tout autre bonus qu’il accumule 
    ce jour-là.
  </p>
  <p>
    Choisissez le bon moment et le bon coureur pour maximiser l’impact de votre Joker. 
    C’est une opportunité précieuse pour faire la <strong>différence</strong> dans le classement général !
  </p>
  <br />
  <h2>4. Transferts</h2>
  <p>
    Vous disposez de <strong>3 changements</strong> pendant la compétition.
  </p>
  <p>
    Ces changements peuvent être effectués <strong>à n’importe quel moment</strong> entre la fin d’une {stage} et le départ de {stageWithArticle} suivante.
  </p>
  <p>
    Ces 3 changements <strong>ne sont pas liés à un abandon ou à un forfait</strong> : vous pouvez également remplacer des coureurs toujours inscrits.
  </p>
  <p>
    Attention : les coureurs entrants coûtent <strong>10 % de plus</strong> qu’au début du jeu!
  </p>
  <br />
  <h2>5. Classements</h2>
  <p>
    Le classement de {stageWithArticle} est établi après chaque {stage}.
  </p>
    <br />
  <p>
   Pour le classement général final, les points de chaque {stage} sont additionnés et 
   complétés par les points bonus des classements finaux par maillot.
  </p>
    <br />
  <p>
   Nous suivons toujours les décisions du jury de course concernant les classements, 
   disqualifications et autres incidents.
  </p>
  <br />
  <h2>6. Général</h2>
  <p>Le règlement général des concours RTBF est d'application pour "Tour de France, le jeu". En cas de litige, la RTBF reste seule juge. Les gains seront uniquement transmis par mail, envoyés en Belgique ou enlevé à la RTBF à Bruxelles.</p>
  <p>
    RTBF se réserve le droit de refuser des utilisateurs et/ou des équipes si 
    ceux-ci ne respectent pas les règles du jeu ou vont à l’encontre de l’esprit 
    du jeu. En cas d’abus, de tromperie ou de fraude (participation via un script, 
    personnes fictives, adresses e-mail fictives, exploitation d’un bug, etc.), 
    RTBF peut supprimer un utilisateur et/ou ses équipes et lui retirer le droit à 
    toute récompense.
  </p>
  <p>&nbsp;</p>
</div>

        <div className="pt-4 text-center">
          <Button
            asChild
            className="rounded-full bg-[var(--rtbf-orange,#fdc300)] text-[var(--rtbf-blue-dark,#003E63)] hover:bg-[#e6b200]"
          >
            <Link href="/">Retour à l&apos;accueil</Link>
          </Button>
        </div>
            </div>
        </section>
      </div>
    </div>
  )
}
